import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * **A strip of tabs that are addresses** — the office dashboard's `.tabs` /
 * `.tab` / `.tab-panel` markup and words (`dashboard/src/routes/organisation/
 * record.tsx`), with its CSS copied across beside them.
 *
 * They are `<Link>`s rather than buttons because each one *is* a page: every
 * section of the org page is its own URL, so an operator can send somebody a
 * settings screen and a reload puts them back on the tab they were reading.
 * That is also why the active tab is decided by the caller from the route rather
 * than held here — there is no tab state in this component, and there is nothing
 * for it to get out of step with.
 *
 * The dashboard uses `NavLink` and lets it work the active class out. Here the
 * tab is a route *parameter* (`/organizations/:orgId/:tab`) rather than a path
 * segment `NavLink` can match on its own, and the caller has already narrowed it
 * to the set it accepts — so it is passed in, and one narrowing decides both
 * which panel renders and which tab is lit.
 *
 * **It is navigation, and it says so.** This looked like a `role="tablist"` of
 * `role="tab"` links carrying `aria-selected`, and that ARIA is a promise about
 * the keyboard: arrow keys move between tabs, one stop for the whole strip, a
 * roving `tabindex`. None of that is here and none of it should be — these are
 * links, and a reader's arrow keys, middle click and "open in new tab" all mean
 * what they normally mean. So the strip is a plain `<nav>` of links and the lit
 * one carries `aria-current="page"`, which is the true sentence: it is the page
 * you are on. The panel under it is a container and nothing more.
 */
export interface TabSpec {
  /** Matched against `active`. */
  key: string;
  label: string;
  to: string;
}

export function Tabs({ tabs, active }: { tabs: readonly TabSpec[]; active: string }) {
  return (
    <nav className="tabs" aria-label="Organization sections">
      {tabs.map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          aria-current={tab.key === active ? 'page' : undefined}
          className={tab.key === active ? 'tab active' : 'tab'}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}

/** The panel under the strip. */
export function TabPanel({ children }: { children: ReactNode }) {
  return <div className="tab-panel">{children}</div>;
}
